import { useCallback, type ReactNode } from "react";
import { Button } from "@salestrak/ui";
import { useIssueView } from "#app/components/issue-view";
import { useCanCreateProject } from "#app/lib/hooks/use-can-create-project";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";

/**
 * The one door an empty entity list offers: create the kind of row it lists.
 * Each hook returns a primary CTA to hand `WidgetEmpty`'s `action` (or
 * `IssueTable`/`IssueList`'s `emptyAction`), or `undefined` when the viewer
 * cannot create — the empty state then stands with its title alone rather than
 * a door that opens a dialog which cannot commit. A primary `Button`, not the
 * kit's ghost `WidgetEmptyAction` row: these sit centered under a grid tile's
 * title as the tile's one call to action, where the quiet row reads as a
 * caption. The gate matches the module-menu create row: a Project needs
 * `create.items` AND a workflow the viewer can create into.
 */
export function useCreateProjectEmptyAction(source: string): ReactNode | undefined {
  const canCreate = useCanCreateProject();
  const { openCreateIssue } = useIssueView();
  // On a team's dashboard the new project starts with that team assigned.
  const scope = useDashboardScope();
  const teamId = scope?.entity?.type === "team" ? scope.entity.id : undefined;
  const onClick = useCallback(
    () => openCreateIssue({ source, ...(teamId ? { teamId } : {}) }),
    [openCreateIssue, source, teamId],
  );
  if (!canCreate) return undefined;
  return (
    // Words alone, like the empty state it sits under.
    <Button size="sm" onClick={onClick}>
      Create Project
    </Button>
  );
}
